import React from "react";
import { ParticipantFullSummary } from "../types/participant.types";
import { RegistrationRequest } from "../types/registration.types";
import { calculateParticipantChanges } from "../utils/participant-utils";
import {
  useListProfessionalForCongressIdQuery,
  useListMealsForCongressIdQuery,
  useListSocialsForCongressIdQuery,
} from "../store/activity/activity";
import {
  ActivityType,
  ActivitySummary as ActivitySummaryType,
} from "../types/activity.types";
import {
  useListPresentationTopicByCongressIdQuery,
  useListPresentationFormByCongressIdQuery,
} from "../store/presentation/presentation";
import { PresentationSummary as PresentationSummaryType } from "../types/presentation.types";
import {
  useListEscortFeesByCongressIdQuery,
  useListParticipantFeesByCongressIdQuery,
} from "../store/fee/fee";
import { useListAccomodationsQuery } from "../store/accomodation/accomodation";
import { ConferenceConfiguration } from "../types/conference.types";
import { useConferenceQuery } from "./useConferenceQuery";

type ActivityChoiceLike = {
  activityId: number;
  count?: number;
  choiceCount?: number;
};

function isPaymentModifiedFieldFilled(
  fieldKey: string,
  modified: unknown,
): boolean {
  if (modified === null || modified === undefined) return false;
  if (fieldKey === "payment") {
    return typeof modified === "string" && modified.length > 0;
  }
  if (typeof modified === "string") {
    return modified.trim().length > 0;
  }
  return false;
}

function paymentChangesIncludeFilledData(
  changes: Record<string, { original: unknown; modified: unknown }>,
): boolean {
  for (const key of Object.keys(changes)) {
    if (!key.startsWith("payment.")) continue;
    const fieldKey = key.slice("payment.".length);
    if (isPaymentModifiedFieldFilled(fieldKey, changes[key]?.modified)) {
      return true;
    }
  }
  return false;
}

/** Csak akkor számít változásnak, ha van érdemi eltérés: új activityId csak count > 0 esetén. */
function activityChoiceDeltaIsSignificant(
  original: ActivityChoiceLike[] | null | undefined,
  modified: ActivityChoiceLike[] | null | undefined,
): boolean {
  const origMap = new Map<number, number>();
  for (const item of original ?? []) {
    const c = item.count ?? item.choiceCount ?? 0;
    origMap.set(item.activityId, c);
  }
  const modMap = new Map<number, number>();
  for (const item of modified ?? []) {
    modMap.set(item.activityId, item.count ?? item.choiceCount ?? 0);
  }
  const allIds = new Set<number>([...origMap.keys(), ...modMap.keys()]);
  for (const id of allIds) {
    const o = origMap.get(id) ?? 0;
    const m = modMap.get(id) ?? 0;
    if (o === m) continue;
    const wasInOriginal = origMap.has(id);
    if (!wasInOriginal && m <= 0) continue;
    return true;
  }
  return false;
}

/** Regisztrációs config + modulok bekapcsolása, ha a change diff szerint érintettek. */
function buildDisplayConfigJson(
  registrationConfig: ConferenceConfiguration | undefined,
  changes: Record<string, { original: unknown; modified: unknown }>,
): ConferenceConfiguration {
  const display: ConferenceConfiguration = {
    mailingDetailsEnabled: !!registrationConfig?.mailingDetailsEnabled,
    paymentDetailsEnabled: !!registrationConfig?.paymentDetailsEnabled,
    participantFeeEnabled: !!registrationConfig?.participantFeeEnabled,
    presentationEnabled: !!registrationConfig?.presentationEnabled,
    lunchEnabled: !!registrationConfig?.lunchEnabled,
    activityEnabled: !!registrationConfig?.activityEnabled,
    accommodationEnabled: !!registrationConfig?.accommodationEnabled,
    otherInfoEnabled: !!registrationConfig?.otherInfoEnabled,
  };

  for (const key of Object.keys(changes)) {
    if (key.startsWith("address.")) {
      display.mailingDetailsEnabled = true;
    } else if (key === "participantFee" || key === "escortFee") {
      display.participantFeeEnabled = true;
    } else if (key === "presentations") {
      display.presentationEnabled = true;
    } else if (key === "hotelBooking") {
      display.accommodationEnabled = true;
    } else if (key === "comment") {
      display.otherInfoEnabled = true;
    }
  }

  if (paymentChangesIncludeFilledData(changes)) {
    display.paymentDetailsEnabled = true;
  }

  const mealsCh = changes["meals"];
  if (
    mealsCh &&
    activityChoiceDeltaIsSignificant(
      mealsCh.original as ActivityChoiceLike[] | null | undefined,
      mealsCh.modified as ActivityChoiceLike[] | null | undefined,
    )
  ) {
    display.lunchEnabled = true;
  }

  const profCh = changes["professionals"];
  if (
    profCh &&
    activityChoiceDeltaIsSignificant(
      profCh.original as ActivityChoiceLike[] | null | undefined,
      profCh.modified as ActivityChoiceLike[] | null | undefined,
    )
  ) {
    display.activityEnabled = true;
  }

  const socialsCh = changes["socials"];
  if (
    socialsCh &&
    activityChoiceDeltaIsSignificant(
      socialsCh.original as ActivityChoiceLike[] | null | undefined,
      socialsCh.modified as ActivityChoiceLike[] | null | undefined,
    )
  ) {
    display.activityEnabled = true;
  }

  return display;
}

interface UseParticipantChangesResult {
  changes: Record<string, { original: any; modified: any }>;
  configJson: ConferenceConfiguration;
  displayConfigJson: ConferenceConfiguration;
  modifiedMeals: ActivitySummaryType[] | null;
  modifiedProfessionals: ActivitySummaryType[] | null;
  modifiedSocials: ActivitySummaryType[] | null;
  modifiedPresentations: PresentationSummaryType[] | null;
  modifiedTotalPrice: number | null;
  totalPriceChanged: boolean;
  allParticipantFees: any[];
  allAccomodations: any[];
}

export const useParticipantChanges = (
  participant: ParticipantFullSummary,
  modifiedData: RegistrationRequest | null | undefined,
  showChanges: boolean,
  registrationId: number,
): UseParticipantChangesResult => {
  const congressId = participant.congressEvent.id;
  const isEditMode = showChanges || !!modifiedData;
  const { congressEvent } = useConferenceQuery(congressId, isEditMode);

  // Calculate changes
  const changes = React.useMemo(() => {
    if (showChanges && modifiedData) {
      return calculateParticipantChanges(participant, modifiedData);
    }
    return {};
  }, [showChanges, modifiedData, participant]);

  const registrationConfig =
    participant.congressEvent.configJson || congressEvent?.configJson;

  const displayConfigJson = React.useMemo(
    () => buildDisplayConfigJson(registrationConfig, changes),
    [registrationConfig, changes],
  );

  // Get activity lists
  const { data: mealsList = [] } = useListMealsForCongressIdQuery(congressId, {
    skip: !showChanges || !modifiedData,
  });
  const { data: professionalsList = [] } =
    useListProfessionalForCongressIdQuery(congressId, {
      skip: !showChanges || !modifiedData,
    });
  const { data: socialsList = [] } = useListSocialsForCongressIdQuery(
    congressId,
    {
      skip: !showChanges || !modifiedData,
    },
  );

  // Convert modified ActivityChoice[] to ActivitySummary[]
  const convertToActivitySummary = React.useCallback(
    (
      activityChoices: { activityId: number; count: number }[] | null,
      activityList: {
        id: number;
        name: string;
        price: number;
        date: Date | string;
        activityType: ActivityType;
      }[],
    ): ActivitySummaryType[] => {
      if (!activityChoices || activityChoices.length === 0) return [];
      return activityChoices
        .map((choice) => {
          const activity = activityList.find((a) => a.id === choice.activityId);
          if (!activity) return null;
          return {
            activityId: activity.id,
            name: activity.name,
            price: activity.price,
            activityType: activity.activityType,
            choiceCount: choice.count,
            date: activity.date,
          };
        })
        .filter((item): item is ActivitySummaryType => item !== null);
    },
    [],
  );

  // Get modified activities if there are changes
  const modifiedMeals = React.useMemo(() => {
    if (showChanges && modifiedData?.meals) {
      return convertToActivitySummary(modifiedData.meals, mealsList);
    }
    return null;
  }, [showChanges, modifiedData, mealsList, convertToActivitySummary]);

  const modifiedProfessionals = React.useMemo(() => {
    if (showChanges && modifiedData?.professionals) {
      return convertToActivitySummary(
        modifiedData.professionals,
        professionalsList,
      );
    }
    return null;
  }, [showChanges, modifiedData, professionalsList, convertToActivitySummary]);

  const modifiedSocials = React.useMemo(() => {
    if (showChanges && modifiedData?.socials) {
      return convertToActivitySummary(modifiedData.socials, socialsList);
    }
    return null;
  }, [showChanges, modifiedData, socialsList, convertToActivitySummary]);

  // Get presentation lists
  const { data: presentationForms = [] } =
    useListPresentationFormByCongressIdQuery(congressId, {
      skip: !showChanges || !modifiedData,
    });
  const { data: presentationTopics = [] } =
    useListPresentationTopicByCongressIdQuery(congressId, {
      skip: !showChanges || !modifiedData,
    });

  // Convert modified PresentationRequest[] to PresentationSummary[]
  const modifiedPresentations = React.useMemo(() => {
    if (
      showChanges &&
      modifiedData?.presentations &&
      changes["presentations"] !== undefined
    ) {
      return modifiedData.presentations.map((pres, index) => {
        const form = presentationForms.find(
          (f) => f.id === pres.presentationTypeId,
        );
        const topic = presentationTopics.find(
          (t) => t.id === pres.presentationTopicId,
        );

        // Try to find existing presentation ID from original data
        // Match by index if the number of presentations is the same
        // Otherwise try to match by content (title, performers, workplace)
        let matchedPresentation: PresentationSummaryType | undefined;

        if (participant.presentations && participant.presentations.length > 0) {
          // If same number of presentations, match by index
          if (
            participant.presentations.length ===
            (modifiedData.presentations?.length || 0)
          ) {
            matchedPresentation = participant.presentations[index];
          } else {
            // Try to match by content
            matchedPresentation = participant.presentations.find(
              (orig) =>
                orig.presentationTitle === pres.presentationTitle &&
                orig.performers === pres.performers &&
                orig.performersWorkplace === pres.performersWorkplace,
            );
          }
        }

        return {
          id: matchedPresentation?.id || 0, // Use existing ID if found, otherwise 0 (new presentation)
          participantId: registrationId,
          presentationType: form?.name || "",
          presentationTopic: topic?.name || "",
          presentationTitle: pres.presentationTitle || "",
          performers: pres.performers || "",
          performersWorkplace: pres.performersWorkplace || "",
        };
      });
    }
    return null;
  }, [
    showChanges,
    modifiedData,
    changes,
    presentationForms,
    presentationTopics,
    registrationId,
    participant.presentations,
  ]);

  // Get fees and accomodations
  const { data: participantFees = [] } =
    useListParticipantFeesByCongressIdQuery(congressId, {
      skip: !showChanges || !modifiedData,
    });
  const { data: escortFees = [] } = useListEscortFeesByCongressIdQuery(
    congressId,
    { skip: !showChanges || !modifiedData },
  );
  const allParticipantFees = React.useMemo(
    () => [...participantFees, ...escortFees],
    [participantFees, escortFees],
  );
  const { data: allAccomodations = [] } = useListAccomodationsQuery(
    congressId,
    {
      skip: !showChanges || !modifiedData,
    },
  );

  // Calculate modified total price
  const calculateModifiedTotalPrice = React.useCallback((): number | null => {
    if (!modifiedData) return null;
    let totalPrice = 0;
    const config = congressEvent?.configJson;

    // Participant Fee
    if (
      config?.participantFeeEnabled &&
      modifiedData.participantFee?.participationFeeId
    ) {
      const fee = allParticipantFees.find(
        (f: { id: number }) =>
          f.id === modifiedData.participantFee?.participationFeeId,
      );
      if (fee) {
        totalPrice += fee.totalPrice;
      }
    }

    // Escort Fee
    if (
      config?.participantFeeEnabled &&
      modifiedData.escortFee?.participationFeeId &&
      modifiedData.escortFee.numberOfAttendants > 0
    ) {
      const fee = allParticipantFees.find(
        (f: { id: number }) =>
          f.id === modifiedData.escortFee?.participationFeeId,
      );
      if (fee) {
        totalPrice +=
          fee.totalPrice * modifiedData.escortFee.numberOfAttendants;
      }
    }
    // else if (
    //   participant.escortFee &&
    //   participant.escortFee.numberOfAttendants > 0 &&
    // ) {
    //   totalPrice += participant.escortFee.totalPrice;
    // }

    // Meal fees
    if (
      config?.lunchEnabled &&
      modifiedData.meals &&
      modifiedData.meals.length > 0
    ) {
      const mealTotal = modifiedData.meals.reduce((sum, meal) => {
        if (meal.count > 0) {
          const activity = mealsList.find((a) => a.id === meal.activityId);
          if (activity) {
            return sum + activity.price * meal.count;
          }
        }
        return sum;
      }, 0);
      totalPrice += mealTotal;
    }

    // Professional Activity fees
    if (
      config?.activityEnabled &&
      modifiedData.professionals &&
      modifiedData.professionals.length > 0
    ) {
      const professionalTotal = modifiedData.professionals.reduce(
        (sum, prof) => {
          if (prof.count > 0) {
            const activity = professionalsList.find(
              (a) => a.id === prof.activityId,
            );
            if (activity) {
              return sum + activity.price * prof.count;
            }
          }
          return sum;
        },
        0,
      );
      totalPrice += professionalTotal;
    }

    // Social Activity fees
    if (
      config?.activityEnabled &&
      modifiedData.socials &&
      modifiedData.socials.length > 0
    ) {
      const socialTotal = modifiedData.socials.reduce((sum, social) => {
        if (social.count > 0) {
          const activity = socialsList.find((a) => a.id === social.activityId);
          if (activity) {
            return sum + activity.price * social.count;
          }
        }
        return sum;
      }, 0);
      totalPrice += socialTotal;
    }

    // Booking fees
    if (
      config?.accommodationEnabled &&
      modifiedData.hotelBooking &&
      modifiedData.hotelBooking.hotelId &&
      modifiedData.hotelBooking.hotelId > 0
    ) {
      const hotel = allAccomodations.find(
        (a: { id: number }) => a.id === modifiedData.hotelBooking?.hotelId,
      );
      if (hotel && modifiedData.hotelBooking.roomId) {
        const room = hotel.hotelRoomList?.find(
          (r: { id: number }) => r.id === modifiedData.hotelBooking?.roomId,
        );
        if (
          room &&
          modifiedData.hotelBooking.arrivalDate &&
          modifiedData.hotelBooking.departureDate
        ) {
          const arrivalDate = new Date(modifiedData.hotelBooking.arrivalDate);
          const departureDate = new Date(
            modifiedData.hotelBooking.departureDate,
          );
          const diffInMillies = Math.abs(
            departureDate.getTime() - arrivalDate.getTime(),
          );
          const noNights = Math.floor(diffInMillies / (1000 * 60 * 60 * 24));
          if (noNights > 0) {
            totalPrice += room.price * noNights;
          }
        }
      }
    }

    return totalPrice;
  }, [
    modifiedData,
    congressEvent?.configJson,
    allParticipantFees,
    mealsList,
    professionalsList,
    socialsList,
    allAccomodations,
    participant.escortFee,
    // displayConfigJson,
  ]);

  const modifiedTotalPrice = React.useMemo(() => {
    if (showChanges && modifiedData) {
      return calculateModifiedTotalPrice();
    }
    return null;
  }, [showChanges, modifiedData, calculateModifiedTotalPrice]);

  const totalPriceChanged = React.useMemo(() => {
    if (!showChanges || !modifiedData || modifiedTotalPrice === null)
      return false;
    const originalTotalPrice = participant.totalPrice || 0;
    return Math.abs(modifiedTotalPrice - originalTotalPrice) > 0.01;
  }, [showChanges, modifiedData, modifiedTotalPrice, participant.totalPrice]);

  return {
    changes,
    configJson: registrationConfig,
    displayConfigJson,
    modifiedMeals,
    modifiedProfessionals,
    modifiedSocials,
    modifiedPresentations,
    modifiedTotalPrice,
    totalPriceChanged,
    allParticipantFees,
    allAccomodations,
  };
};
